import { locale } from "../../i18n/i18n";

/** Largeurs doctrinales des fuseaux par clé de gabarit (docs/15-fuseaux-doctrinaux.md §2.1, §2.4, registres L1 et
 *  L4), jumeau de `generex_engine/corridor_widths.py` vérifié par `__fixtures__/validate.json`
 *  (`constants.width_doctrine`).
 *
 *  Source : planches « Mémento EDG-T / Fuseaux et lignes » (Mémento tactique EDG p. 24, 122, 131, 136-137).
 *  Chaque clé de gabarit (`deployment.level_key`) donne la largeur par défaut (fuseau automatique, amorces,
 *  aimantation : médiane de la plage, son haut pour le GTIA blindé, son bas pour la division), la plage
 *  doctrinale (alerte) et les bornes dures de la règle « width », qui contiennent les anciennes bornes de l'iPad
 *  (0,25 à 3 fois 18,15 / 40 / 60 km) : aucun fuseau enregistré n'est invalidé.
 *
 *  Alerte de largeur (identique au serveur, `user_corridor._warnings`) : une paire alerte si elle est retouchée
 *  (plus de 1 % d'écart à sa largeur de référence) et si la retouche l'éloigne de la plage ; sans référence
 *  (fuseau antérieur, zone libre), dès qu'elle sort de la plage ; sans clé de gabarit du niveau, jamais. */

export type LevelKey = "REGIMENT_INF" | "REGIMENT_ARMOR" | "BRIGADE" | "DIVISION" | "COMPAGNIE_INF" | "COMPAGNIE_ARMOR" | "CORPS";

export interface WidthDoctrine {
  /** Clé de gabarit, ou niveau pour l'union des familles (clé inconnue). */
  key: string;
  /** Largeur par défaut : fuseau automatique, amorces, aimantation (m). */
  default_m: number;
  /** Plage doctrinale (alerte), en mètres. */
  min_m: number;
  max_m: number;
  /** Largeur de l'iPad (2 × SEPARATION_M du niveau) : anciennes bornes. */
  ipad_m: number;
  /** Bornes dures de la règle « width » (m). */
  hard_min_m: number;
  hard_max_m: number;
  /** Largeurs sans clé de gabarit du niveau (absente ou d'un autre niveau) : union des familles du niveau, ou sa
   *  famille unique en brigade et en division ; aucune alerte (`alertPairs` se tait, docs/15 §10.12). */
  union: boolean;
}

/** Bornes dures : [min(0,25 × min ; 0,25 × iPad) ; max(3 × max ; 3 × iPad)]. */
export const HARD_MIN_RATIO = 0.25;
export const HARD_MAX_RATIO = 3;
/** Plancher des tolérances géométriques de la validation (virage, orientation, LIMA dans le fuseau) : sans lui,
 *  la règle « virage » rejetterait des amorces de GTIA d'infanterie (8 km). */
export const GEOMETRY_SCALE_FLOOR_M = 18_150;
/** Une paire est « retouchée » quand sa largeur s'écarte de plus de 1 % de sa référence. */
export const MODIFIED_RATIO = 0.01;

function doctrine(key: string, defaultM: number, minM: number, maxM: number, ipadM: number): WidthDoctrine {
  return {
    key,
    default_m: defaultM,
    min_m: minM,
    max_m: maxM,
    ipad_m: ipadM,
    hard_min_m: Math.min(HARD_MIN_RATIO * minM, HARD_MIN_RATIO * ipadM),
    hard_max_m: Math.max(HARD_MAX_RATIO * maxM, HARD_MAX_RATIO * ipadM),
    union: false,
  };
}

export const WIDTHS: Readonly<Record<LevelKey, WidthDoctrine>> = {
  REGIMENT_INF: doctrine("REGIMENT_INF", 8_000, 6_000, 10_000, 18_150), // p. 131
  REGIMENT_ARMOR: doctrine("REGIMENT_ARMOR", 30_000, 5_000, 30_000, 18_150), // p. 136-137
  BRIGADE: doctrine("BRIGADE", 20_000, 10_000, 30_000, 40_000), // p. 122
  // Division : bas de la plage (décision d'Alexis du 14/10/2026) ; le mémento donne des maxima (p. 13, 24, 51).
  DIVISION: doctrine("DIVISION", 60_000, 60_000, 120_000, 60_000), // p. 24
  // Compagnie ou escadron (fiche validée par Alexis, docs/21 §3) : pas de référence iPad, largeur iPad = défaut.
  COMPAGNIE_INF: doctrine("COMPAGNIE_INF", 3_000, 1_500, 5_000, 3_000),
  COMPAGNIE_ARMOR: doctrine("COMPAGNIE_ARMOR", 5_000, 2_000, 10_000, 5_000),
  // Corps d'armée (décision d'Alexis du 16/10/2026, docs/08 §23) : 300 km, milieu de la plage du mémento (200 à 400 km,
  // p. 46) ; pas de référence iPad, largeur iPad = défaut.
  CORPS: doctrine("CORPS", 300_000, 200_000, 400_000, 300_000),
};

/** Familles de gabarit de chaque niveau (dans l'ordre : la première donne le défaut de l'union). */
export const LEVEL_KEYS: Readonly<Record<string, readonly LevelKey[]>> = {
  REGIMENT: ["REGIMENT_INF", "REGIMENT_ARMOR"],
  BRIGADE: ["BRIGADE"],
  DIVISION: ["DIVISION"],
  COMPAGNIE: ["COMPAGNIE_INF", "COMPAGNIE_ARMOR"],
  CORPS: ["CORPS"],
};

const own = (record: object, key: string) => Object.prototype.hasOwnProperty.call(record, key);

export function isLevelKey(key: unknown): key is LevelKey {
  return typeof key === "string" && own(WIDTHS, key);
}

/** Largeurs d'une clé de gabarit. */
export function width(levelKey: LevelKey): WidthDoctrine {
  return WIDTHS[levelKey];
}

/** La clé de gabarit appartient-elle au niveau (largeurs propres, alerte de largeur) ? */
export function keyed(level: string, levelKey: string | null | undefined): levelKey is LevelKey {
  return own(LEVEL_KEYS, level) && isLevelKey(levelKey) && LEVEL_KEYS[level]!.includes(levelKey);
}

/** Largeurs d'un niveau (`corridor_widths.for_level`) : celles de `levelKey` s'il appartient au niveau ; sinon
 *  l'union des familles du niveau (plage et bornes dures les plus larges, défaut de la première famille), ou sa
 *  famille unique (brigade, division), marquées `union` : aucune alerte ; null pour un niveau sans fuseau
 *  doctrinal (CENTAC). */
export function forLevel(level: string, levelKey?: string | null): WidthDoctrine | null {
  if (!own(LEVEL_KEYS, level)) return null;
  if (keyed(level, levelKey)) return WIDTHS[levelKey];
  const family = LEVEL_KEYS[level]!.map((k) => WIDTHS[k]);
  // Famille unique sans sa clé : mêmes largeurs, mais l'alerte se tait (`user_corridor._warnings`).
  if (family.length === 1) return { ...family[0]!, union: true };
  return {
    key: level,
    default_m: family[0]!.default_m,
    min_m: Math.min(...family.map((d) => d.min_m)),
    max_m: Math.max(...family.map((d) => d.max_m)),
    ipad_m: family[0]!.ipad_m,
    hard_min_m: Math.min(...family.map((d) => d.hard_min_m)),
    hard_max_m: Math.max(...family.map((d) => d.hard_max_m)),
    union: true,
  };
}

/** Écart d'une largeur à la plage doctrinale : max(0, min − w, w − max), en mètres. */
export function rangeGap(w: number, d: WidthDoctrine): number {
  return Math.max(0, d.min_m - w, w - d.max_m);
}

/** Largeur admise par la règle « width » de la validation : bornes dures comprises. */
export function withinHardBounds(w: number, d: WidthDoctrine): boolean {
  return d.hard_min_m <= w && w <= d.hard_max_m;
}

/** Paire retouchée : plus de 1 % d'écart à sa largeur de référence. */
export function retouched(w: number, reference: number): boolean {
  return reference > 0 && Math.abs(w / reference - 1) > MODIFIED_RATIO;
}

/** Paires en alerte (règle du §2.4), dans l'ordre : `references` (largeur de référence de chaque paire) null pour
 *  un fuseau sans base « mémento » (fuseau antérieur, zone libre) : toute paire hors de la plage ; sinon, une
 *  paire retouchée que la retouche éloigne de la plage : gap(w) > gap(w_ref) + 0,01 × w_ref. */
export function alertPairs(found: readonly number[], d: WidthDoctrine, references: readonly number[] | null): number[] {
  if (d.union) return [];
  return found.flatMap((w, i) => {
    if (!references) return rangeGap(w, d) > 0 ? [i] : [];
    const ref = references[i] ?? NaN;
    return retouched(w, ref) && rangeGap(w, d) > rangeGap(ref, d) + MODIFIED_RATIO * ref ? [i] : [];
  });
}

/** Largeur de la pire paire en alerte (plus grand écart à la plage, la première à égalité), ou null. */
export function worstWidth(found: readonly number[], alerts: readonly number[], d: WidthDoctrine): number | null {
  let worst: number | null = null;
  for (const i of alerts) {
    const w = found[i]!;
    if (worst === null || rangeGap(w, d) > rangeGap(worst, d)) worst = w;
  }
  return worst;
}

/** Message du serveur (`user_corridor.WIDTH_WARNING`), traduit côté client par `tServer`. */
export const WIDTH_WARNING = "Largeur hors de la plage doctrinale ({min} à {max} km) : {largeur} km";

/** Kilomètres à la française du serveur (`user_corridor._km`) : entier sans décimale (« 6 »), sinon deux
 *  décimales (« 12,40 ») ; `decimals` impose le nombre de décimales. */
export function serverKm(metres: number, decimals?: number): string {
  const km = metres / 1000;
  if (decimals === undefined && Math.abs(km - Math.round(km)) < 1e-9) return String(Math.round(km));
  return km.toFixed(decimals ?? 2).replace(".", ",");
}

/** Largeur affichée, dans la langue de l'interface : deux décimales, en km (« 18,15 km »). Carte (puce du geste,
 *  annonce du clavier) et panneau Manœuvre. */
export function formatKm(metres: number): string {
  return `${(metres / 1000).toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
}

/** Borne de largeur en km, dans la langue de l'interface, sans unité ni zéro inutile (« 6 », « 1,5 », « 54,45 ») :
 *  plage doctrinale et bornes dures (docs/15 §2.4), sur la carte comme dans le panneau. */
export function formatKmBound(metres: number): string {
  return (metres / 1000).toLocaleString(locale(), { maximumFractionDigits: 2 });
}

/** Avertissement de largeur pour la pire paire, au texte exact du serveur. */
export function widthWarning(d: WidthDoctrine, worst: number): string {
  return WIDTH_WARNING.replace("{min}", serverKm(d.min_m))
    .replace("{max}", serverKm(d.max_m))
    .replace("{largeur}", serverKm(worst, 2));
}
